// Seasons on the map (Design §4.1): the floor's colour, ground patches, holes in the ice, ruts
// in deep snow and falling snow. The rules are in the simulation (`sim::terrain`); this only
// draws them.

/** `Season::to_u8`. */
export const SPRING = 1;
export const SUMMER = 2;
export const AUTUMN = 3;
export const WINTER = 4;
/** Effect bits per season (`sim::terrain`): winter's snowfall. */
export const SNOWFALL = 8;
/** Patch kinds as sent (`terrain_patches`). */
export const [ICE, SNOW, LAKE, MUD, PUDDLE, LEAVES, FOG_BANK, HEDGE, WATER, SAND, HEAT, GRASS, HILL] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Floor colour without a season and in each. */
export function floorColor(season: number | undefined): string {
  return season === WINTER ? '#d6e0e7' : season === AUTUMN ? '#cfc1a2' : season === SPRING ? '#bccf9c' : season === SUMMER ? '#d3c98f' : '#d9d4c7';
}

/** Runs `each(kind, start)` for every patch of `patches` (kind, corners, x, y, …). */
function eachPatch(patches: Float32Array, each: (kind: number, at: number, corners: number) => void): void {
  for (let i = 0; i < patches.length; ) {
    const kind = patches[i];
    const corners = patches[i + 1];
    each(kind, i + 2, corners);
    i += 2 + 2 * corners;
  }
}

/** Middle and radius of the patch at `at` with `corners`. */
function middle(p: Float32Array, at: number, corners: number): [number, number, number] {
  let [x, y] = [0, 0];
  for (let k = 0; k < corners; k++) [x, y] = [x + p[at + 2 * k] / corners, y + p[at + 2 * k + 1] / corners];
  let r = 0;
  for (let k = 0; k < corners; k++) r = Math.max(r, Math.hypot(p[at + 2 * k] - x, p[at + 2 * k + 1] - y));
  return [x, y, r];
}

/** Only the patches of `kind`. */
export function patchesOf(patches: Float32Array, kind: number): Float32Array {
  const out: number[] = [];
  eachPatch(patches, (k, at, corners) => {
    if (k === kind) out.push(k, corners, ...patches.subarray(at, at + 2 * corners));
  });
  return Float32Array.from(out);
}

/** The patch of `kind` nearest to `x`, `y` (kind, corners, x, y, …) with its middle and radius, if there is one. */
export function nearestPatch(patches: Float32Array, kind: number, x: number, y: number): { patch: Float32Array; x: number; y: number; r: number } | undefined {
  let found: { patch: Float32Array; x: number; y: number; r: number } | undefined;
  eachPatch(patches, (k, at, corners) => {
    if (k !== kind) return;
    const [mx, my, r] = middle(patches, at, corners);
    if (!found || Math.hypot(mx - x, my - y) < Math.hypot(found.x - x, found.y - y)) found = { patch: patches.slice(at - 2, at + 2 * corners), x: mx, y: my, r };
  });
  return found;
}

function patchPath(ctx: CanvasRenderingContext2D, p: Float32Array, at: number, corners: number): void {
  ctx.beginPath();
  for (let k = 0; k < corners; k++) ctx.lineTo(p[at + 2 * k], p[at + 2 * k + 1]);
  ctx.closePath();
}

/** Ground patches, on the floor below the obstacles (drawn once per map into the ground layer). */
export function drawPatches(ctx: CanvasRenderingContext2D, patches: Float32Array): void {
  ctx.save();
  drawBrook(ctx, patches);
  eachPatch(patches, (kind, at, corners) => {
    if (kind === LEAVES || kind === FOG_BANK || kind === GRASS) return; // over the tanks, see `drawCover`
    patchPath(ctx, patches, at, corners);
    if (kind === SAND) {
      // Sand: pale dunes with wind ripples.
      ctx.fillStyle = '#e8d6a0';
      ctx.fill();
      ctx.strokeStyle = '#d6bf80';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.save();
      ctx.clip();
      const [x, y, r] = middle(patches, at, corners);
      ctx.strokeStyle = '#cfb574';
      ctx.lineWidth = 1.5;
      for (let k = -4; k <= 4; k++) {
        ctx.beginPath();
        for (let u = -r; u <= r; u += 8) ctx.lineTo(x + u, y + k * r * 0.22 + Math.sin(u / 14 + k) * 4);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    if (kind === HEAT) {
      // Hot ground: sun-bleached, a little orange (the shimmer itself moves, see `drawHeat`).
      ctx.fillStyle = 'rgba(235, 135, 65, 0.38)';
      ctx.fill();
      return;
    }
    if (kind === HILL) {
      // A hill: lit from the top left, shadowed down the slope, with contour lines.
      const [x, y, r] = middle(patches, at, corners);
      const shade = ctx.createRadialGradient(x - r * 0.25, y - r * 0.25, r * 0.1, x, y, r);
      shade.addColorStop(0, 'rgba(255, 255, 240, 0.32)');
      shade.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
      shade.addColorStop(1, 'rgba(40, 30, 10, 0.25)');
      ctx.fillStyle = shade;
      ctx.fill();
      ctx.strokeStyle = 'rgba(60, 45, 20, 0.35)';
      ctx.lineWidth = 1.2;
      for (const k of [1, 0.7, 0.4]) {
        ctx.beginPath();
        for (let c = 0; c < corners; c++) ctx.lineTo(x + (patches[at + 2 * c] - x) * k, y + (patches[at + 2 * c + 1] - y) * k);
        ctx.closePath();
        ctx.stroke();
      }
      return;
    }
    if (kind === WATER) {
      if (corners === 4) return; // a piece of the brook: all of it at once, below
      // The pond: deep in the middle, a light shallow edge, foam on the shore.
      const [x, y, r] = middle(patches, at, corners);
      const deep = ctx.createRadialGradient(x, y, r * 0.1, x, y, r);
      deep.addColorStop(0, DEEP_WATER);
      deep.addColorStop(0.55, '#2f6488');
      deep.addColorStop(1, SHALLOW_WATER);
      ctx.strokeStyle = FOAM;
      ctx.lineWidth = 6;
      ctx.stroke();
      ctx.fillStyle = deep;
      ctx.fill();
      return;
    }
    if (kind === HEDGE) {
      // A flowering hedge: dark green bushes with blossoms.
      ctx.fillStyle = '#3f6d32';
      ctx.fill();
      ctx.strokeStyle = '#2c4f23';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.save();
      ctx.clip();
      const [x, y, r] = middle(patches, at, corners);
      for (let k = 0; k < 24; k++) {
        const a = k * 2.399;
        const d = r * Math.sqrt(k / 24);
        ctx.fillStyle = ['#f4f1f5', '#f2b6cf', '#f5e07a'][k % 3];
        ctx.beginPath();
        ctx.arc(x + Math.cos(a) * d, y + Math.sin(a) * d, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      return;
    }
    if (kind === MUD) {
      // Mud: brown with wet, lighter puddles of slime (lighter since 2026-10-21, Wunsch User).
      ctx.fillStyle = '#8c6c48';
      ctx.fill();
      ctx.strokeStyle = '#6a5034';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.save();
      ctx.clip();
      const [x, y] = middle(patches, at, corners);
      ctx.fillStyle = '#a3845c';
      for (let k = 0; k < 7; k++) {
        ctx.beginPath();
        ctx.ellipse(x + Math.sin(k * 2.3 + x) * 50, y + Math.cos(k * 1.9 + y) * 45, 10 + (k % 3) * 6, 5 + (k % 2) * 3, k, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      return;
    }
    if (kind === PUDDLE) {
      // A puddle: grey-blue water reflecting the sky, a muddy rim.
      ctx.fillStyle = '#7b98ab';
      ctx.fill();
      ctx.strokeStyle = '#8a7350';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = '#c8dbe6aa';
      ctx.lineWidth = 2;
      const [x, y] = middle(patches, at, corners);
      ctx.beginPath();
      ctx.moveTo(x - 20, y - 6);
      ctx.lineTo(x + 6, y - 14);
      ctx.moveTo(x - 6, y + 8);
      ctx.lineTo(x + 18, y);
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (kind === SNOW) {
      // Deep snow: white with a soft blue-grey rim and drifts.
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.strokeStyle = '#c3d1db';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = '#dfe8ee';
      ctx.lineWidth = 2;
      const [x, y] = middle(patches, at, corners);
      for (let k = 0; k < 9; k++) {
        const dx = Math.sin(k * 2.3 + x) * 60;
        const dy = Math.cos(k * 1.7 + y) * 60;
        ctx.beginPath();
        ctx.arc(x + dx, y + dy, 9 + (k % 3) * 5, Math.PI * 1.1, Math.PI * 1.9);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    // Ice and the frozen lake: smooth blue with light streaks, the lake deeper with cracks.
    ctx.fillStyle = kind === LAKE ? '#8fbcd6' : '#c4e4f2';
    ctx.fill();
    ctx.strokeStyle = kind === LAKE ? '#5f93b2' : '#9fcbe0';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.save();
    ctx.clip();
    const [x, y] = [patches[at], patches[at + 1]];
    ctx.strokeStyle = kind === LAKE ? '#d7ecf7aa' : '#ffffffcc';
    ctx.lineWidth = 1.5;
    for (let k = 0; k < 6; k++) {
      // Streaks (ice) or cracks (lake) from one corner across, the same every time.
      const a = k * 1.1 + x * 0.01;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * 160, y + Math.sin(a) * 160);
      if (kind === LAKE) ctx.lineTo(x + Math.cos(a + 0.3) * 220, y + Math.sin(a + 0.3) * 220);
      ctx.stroke();
    }
    ctx.restore();
  });
  ctx.restore();
}

/** Water that drowns (Design §4.1): its colours from the deep middle to the shore. */
const DEEP_WATER = '#1f4b6e';
const SHALLOW_WATER = '#5b93b5';
const FOAM = '#eef6f8';

/** The brook's middle line, a run per stretch between fords, and its width. */
function brookRuns(patches: Float32Array): { runs: number[][]; width: number } {
  const runs: number[][] = [];
  let width = 0;
  eachPatch(patches, (kind, at, corners) => {
    if (kind !== WATER || corners !== 4) return;
    // Pieces as `sim::terrain::brook` makes them: a − across, b − across, b + across, a + across.
    const p = (k: number) => [patches[at + 2 * k], patches[at + 2 * k + 1]];
    const [p0, p1, p2, p3] = [p(0), p(1), p(2), p(3)];
    const [ax, ay, bx, by] = [(p0[0] + p3[0]) / 2, (p0[1] + p3[1]) / 2, (p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
    width = Math.hypot(p0[0] - p3[0], p0[1] - p3[1]);
    const run = runs[runs.length - 1];
    if (run && Math.hypot(run[run.length - 2] - ax, run[run.length - 1] - ay) < 1) run.push(bx, by);
    else runs.push([ax, ay, bx, by]);
  });
  return { runs, width };
}

function runPath(ctx: CanvasRenderingContext2D, run: number[]): void {
  ctx.beginPath();
  for (let k = 0; k < run.length; k += 2) ctx.lineTo(run[k], run[k + 1]);
}

/**
 * The brook as one stream: foam on the banks, shallow at the edges, deep in the middle. Where it
 * meets a ford, a small stone arch, as if it flowed on under the ford (Design §4.1).
 */
function drawBrook(ctx: CanvasRenderingContext2D, patches: Float32Array): void {
  const { runs, width } = brookRuns(patches);
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'butt';
  for (const [color, w] of [
    [FOAM, width + 6],
    [SHALLOW_WATER, width],
    ['#2f6488', width * 0.62],
    [DEEP_WATER, width * 0.3],
  ] as [string, number][]) {
    ctx.strokeStyle = color;
    ctx.lineWidth = w;
    for (const run of runs) {
      runPath(ctx, run);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** The arches at both ends of every ford (drawn with the moving water, over its streaks). */
function drawArches(ctx: CanvasRenderingContext2D, runs: number[][], width: number): void {
  for (let k = 0; k + 1 < runs.length; k++) {
    const [run, next] = [runs[k], runs[k + 1]];
    const [ax, ay, bx, by] = [run[run.length - 2], run[run.length - 1], next[0], next[1]];
    drawArch(ctx, ax, ay, bx - ax, by - ay, width);
    drawArch(ctx, bx, by, ax - bx, ay - by, width);
  }
}

/**
 * A stone arch where the brook ends at a ford: at `x`, `y` across the brook (`width`), the ford
 * lying towards `dx`, `dy`; its hollow side to the water, which runs on into it.
 */
function drawArch(ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number, width: number): void {
  const len = Math.hypot(dx, dy) || 1;
  // A little into the water, so no seam shows where the brook ends.
  [x, y] = [x - (dx / len) * 2, y - (dy / len) * 2];
  // Across the brook; the ellipse's half at angles π..2π then lies towards the ford.
  const turn = Math.atan2(dx / len, -dy / len);
  const half = (rx: number, ry: number) => {
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, turn, Math.PI, 2 * Math.PI);
    ctx.closePath();
  };
  const [outer, inner] = [width / 2 + 4, width / 2 - 1];
  half(outer, 13);
  ctx.fillStyle = '#a59c88';
  ctx.fill();
  ctx.beginPath(); // the outline round the arch only, not across the water
  ctx.ellipse(x, y, outer, 13, turn, Math.PI, 2 * Math.PI);
  ctx.strokeStyle = '#5e574b';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  // The water in its hollow, the brook's bands on.
  for (const [color, rx, ry] of [
    [SHALLOW_WATER, inner, 8],
    ['#2f6488', width * 0.31, 6],
    [DEEP_WATER, width * 0.15, 4],
  ] as [string, number, number][]) {
    half(rx, ry);
    ctx.fillStyle = color;
    ctx.fill();
  }
  // The stones of the arch.
  ctx.strokeStyle = 'rgba(70, 62, 50, 0.7)';
  ctx.lineWidth = 1;
  const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
  const point = (rx: number, ry: number, t: number): [number, number] => [x + cos * rx * Math.cos(t) - sin * ry * Math.sin(t), y + sin * rx * Math.cos(t) + cos * ry * Math.sin(t)];
  for (let k = 1; k < 7; k++) {
    const t = Math.PI + (k / 7) * Math.PI;
    ctx.beginPath();
    ctx.moveTo(...point(inner, 8, t));
    ctx.lineTo(...point(outer, 13, t));
    ctx.stroke();
  }
}

/** Water moving, drawn each frame over the ground: the brook flows, the pond ripples. */
export function drawWater(ctx: CanvasRenderingContext2D, patches: Float32Array | undefined, now: number): void {
  if (!patches?.length) return;
  ctx.save();
  const { runs, width } = brookRuns(patches);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const [side, dash, speed] of [
    [0, 14, 30],
    [-0.28, 9, 38],
    [0.28, 9, 34],
  ]) {
    ctx.setLineDash([dash, dash * 2.2]);
    ctx.lineDashOffset = -now / speed;
    ctx.strokeStyle = side ? 'rgba(200, 228, 242, 0.4)' : 'rgba(220, 240, 250, 0.55)';
    ctx.lineWidth = side ? 1.5 : 2;
    for (const run of runs) {
      ctx.beginPath();
      for (let k = 0; k < run.length; k += 2) {
        // Sideways off the middle line, square to it.
        const [i0, i1] = [Math.max(0, k - 2), Math.min(run.length - 2, k + 2)];
        const [dx, dy] = [run[i1] - run[i0], run[i1 + 1] - run[i0 + 1]];
        const len = Math.hypot(dx, dy) || 1;
        ctx.lineTo(run[k] - (dy / len) * side * width, run[k + 1] + (dx / len) * side * width);
      }
      ctx.stroke();
    }
  }
  ctx.setLineDash([]);
  drawArches(ctx, runs, width);
  eachPatch(patches, (kind, at, corners) => {
    if (kind !== WATER || corners === 4) return;
    // The pond: small waves drifting to and fro.
    ctx.save();
    patchPath(ctx, patches, at, corners);
    ctx.clip();
    const [x, y, r] = middle(patches, at, corners);
    ctx.lineWidth = 1.5;
    const count = Math.round((r * r) / 500);
    for (let k = 0; k < count; k++) {
      const a = k * 2.399;
      const d = r * 0.85 * Math.sqrt((k + 0.5) / count);
      const t = now / 1000 + k * 1.7;
      const [wx, wy] = [x + Math.cos(a) * d + Math.sin(t * 0.5) * 5, y + Math.sin(a) * d + Math.cos(t * 0.4) * 2];
      ctx.strokeStyle = `rgba(215, 236, 248, ${0.25 + 0.25 * Math.sin(t)})`;
      ctx.beginPath();
      ctx.moveTo(wx - 7, wy);
      ctx.quadraticCurveTo(wx - 3.5, wy - 3, wx, wy);
      ctx.quadraticCurveTo(wx + 3.5, wy + 3, wx + 7, wy);
      ctx.stroke();
    }
    ctx.restore();
  });
  ctx.restore();
}

/** Open water in the broken ice: deep dark water, a jagged rim of broken ice with cracks running
 * off it, floes drifting in it, slow ripples. */
export function drawHoles(ctx: CanvasRenderingContext2D, holes: Float32Array, radius: number, now: number): void {
  ctx.save();
  for (let i = 0; i < holes.length; i += 2) {
    const [x, y] = [holes[i], holes[i + 1]];
    // Cracks in the ice around it.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 1.2;
    for (let k = 0; k < 7; k++) {
      const a = k * 0.9 + x * 0.37;
      const len = radius * (0.25 + 0.25 * Math.abs(Math.sin(k * 3.1 + y)));
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * radius, y + Math.sin(a) * radius);
      ctx.lineTo(x + Math.cos(a + 0.12) * (radius + len * 0.6), y + Math.sin(a + 0.12) * (radius + len * 0.6));
      ctx.lineTo(x + Math.cos(a - 0.05) * (radius + len), y + Math.sin(a - 0.05) * (radius + len));
      ctx.stroke();
    }
    // A jagged edge: shards in and out.
    ctx.beginPath();
    for (let k = 0; k < 22; k++) {
      const a = (k / 22) * Math.PI * 2;
      const r = radius * (k % 2 ? 0.86 + 0.06 * Math.sin(k * 1.3 + x) : 0.97 + 0.05 * Math.sin(k * 2.7 + y));
      ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    }
    ctx.closePath();
    const deep = ctx.createRadialGradient(x, y, radius * 0.1, x, y, radius);
    deep.addColorStop(0, '#0b2233');
    deep.addColorStop(1, '#2b5a7a');
    ctx.fillStyle = deep;
    ctx.fill();
    ctx.strokeStyle = '#f4fbff';
    ctx.lineWidth = 3.5;
    ctx.stroke();
    ctx.save();
    ctx.clip();
    const ripple = ((now / 1600 + i) % 1) * radius * 0.8;
    ctx.beginPath();
    ctx.arc(x, y, ripple, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(160, 200, 225, ${0.5 * (1 - ripple / radius)})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Floes of the broken ice, drifting round.
    for (let k = 0; k < 3; k++) {
      const a = k * 2.1 + x + now / 9000;
      const [fx, fy] = [x + Math.cos(a) * radius * 0.55, y + Math.sin(a) * radius * 0.55];
      const s = radius * (0.1 + 0.04 * k);
      ctx.beginPath();
      for (let c = 0; c < 5; c++) {
        const b = (c / 5) * Math.PI * 2 + k + now / 7000;
        ctx.lineTo(fx + Math.cos(b) * s * (0.7 + 0.3 * Math.sin(c * 2.3 + k)), fy + Math.sin(b) * s * (0.7 + 0.3 * Math.sin(c * 2.3 + k)));
      }
      ctx.closePath();
      ctx.fillStyle = '#e8f3f9';
      ctx.fill();
      ctx.strokeStyle = '#9fc3d8';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.restore();
  }
  ctx.restore();
}

const LEAF_COLORS = ['#c0571d', '#d9862b', '#e2b23a', '#9b3b1c', '#b9702a'];
/** Snow flakes and wind streaks come in this many steps of opacity, one path each. */
const WEATHER_STEPS = 4;

/** Piles of leaves (half hide who is in them) and the wandering fog banks (`fogBanks`, polygons as walls: hide who is in them), over the tanks. */
export function drawCover(ctx: CanvasRenderingContext2D, patches: Float32Array | undefined, fogBanks: Float32Array | undefined, now: number): void {
  if (fogBanks?.length) drawFogBanks(ctx, fogBanks, now);
  if (!patches) return;
  ctx.save();
  // Both are drawn into a picture per patch and only that is put on the map: the leaves never
  // change, the grass sways so slowly that redrawing it 15 times a second is enough.
  eachPatch(patches, (kind, at, corners) => {
    if (kind === GRASS) patchSprite(ctx, patches, at, corners, now, GRASS_REDRAW_MS, drawGrass);
    else if (kind === LEAVES) patchSprite(ctx, patches, at, corners, now, Infinity, drawLeafPile);
  });
  ctx.restore();
}

/** Tall grass: blades over whoever is in it (the others don't see them at all). */
function drawGrass(ctx: CanvasRenderingContext2D, patches: Float32Array, at: number, corners: number, now: number): void {
  patchPath(ctx, patches, at, corners);
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = '#7e9a3c';
  ctx.fill();
  ctx.clip();
  ctx.globalAlpha = 0.9;
  ctx.lineWidth = 1.6;
  const [x, y, r] = middle(patches, at, corners);
  const count = Math.round((r * r) / 30);
  // One path per colour, not a stroke per blade.
  for (const [color, light] of [['#5d7a26', false], ['#a3b84e', true]] as const) {
    ctx.beginPath();
    for (let k = 0; k < count; k++) {
      if ((k % 3 === 0) !== light) continue;
      const a = k * 2.399;
      const d = r * Math.sqrt(k / count);
      const [bx, by] = [x + Math.cos(a) * d, y + Math.sin(a) * d];
      const sway = Math.sin(now / 700 + k) * 2.5;
      ctx.moveTo(bx, by + 6);
      ctx.quadraticCurveTo(bx + sway * 0.5, by, bx + sway, by - 7);
    }
    ctx.strokeStyle = color;
    ctx.stroke();
  }
}

/** A pile of leaves (half hides who is in it). */
function drawLeafPile(ctx: CanvasRenderingContext2D, patches: Float32Array, at: number, corners: number): void {
  patchPath(ctx, patches, at, corners);
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = '#9a5a22';
  ctx.fill();
  ctx.clip();
  ctx.globalAlpha = 0.8;
  const [x, y, r] = middle(patches, at, corners);
  const count = Math.round((r * r) / 45);
  for (let k = 0; k < count; k++) {
    // The same leaves every time, spread evenly (golden angle): positions and turns from the index.
    const a = k * 2.399;
    const d = r * Math.sqrt(k / count);
    ctx.save();
    ctx.translate(x + Math.cos(a) * d, y + Math.sin(a) * d);
    ctx.rotate(a * 3.1);
    ctx.fillStyle = LEAF_COLORS[k % LEAF_COLORS.length];
    ctx.beginPath();
    ctx.ellipse(0, 0, 7, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

/** Grass sways at most ~4 units/s: redrawn this often, it moves well under a pixel per step. */
const GRASS_REDRAW_MS = 66;

type Sprite = { canvas: HTMLCanvasElement; x: number; y: number; size: number; scale: number; drawn: number };
/** Pictures of cover patches, per patch list and start index. */
const sprites = new WeakMap<Float32Array, Map<number, Sprite>>();

/** Draws the patch at `at` through its picture, painting that anew at another scale or after `redrawMs`. */
function patchSprite(
  ctx: CanvasRenderingContext2D,
  patches: Float32Array,
  at: number,
  corners: number,
  now: number,
  redrawMs: number,
  paint: (ctx: CanvasRenderingContext2D, patches: Float32Array, at: number, corners: number, now: number) => void,
): void {
  let list = sprites.get(patches);
  if (!list) sprites.set(patches, (list = new Map()));
  const m = ctx.getTransform();
  const scale = Math.hypot(m.a, m.b);
  let sprite = list.get(at);
  if (!sprite || sprite.scale !== scale || now - sprite.drawn >= redrawMs) {
    const [x, y, r] = middle(patches, at, corners);
    const pad = 8; // blades and leaves reach past the corners' circle only up to the clip
    const canvas = sprite?.canvas ?? document.createElement('canvas');
    const px = Math.ceil(2 * (r + pad) * scale);
    if (canvas.width !== px) canvas.width = canvas.height = px;
    const g = canvas.getContext('2d')!;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, px, px);
    g.save();
    g.setTransform(scale, 0, 0, scale, (pad + r - x) * scale, (pad + r - y) * scale);
    paint(g, patches, at, corners, now);
    g.restore();
    // Redraws of the patches spread over the frames instead of all in one.
    const drawn = sprite ? now : now - ((at * 7) % (Number.isFinite(redrawMs) ? redrawMs : 1));
    sprite = { canvas, x: x - r - pad, y: y - r - pad, size: px / scale, scale, drawn };
    list.set(at, sprite);
  }
  ctx.drawImage(sprite.canvas, sprite.x, sprite.y, sprite.size, sprite.size);
}

/** Rings on the water around the living tanks in puddles. */
export function drawSplashes(ctx: CanvasRenderingContext2D, patches: Float32Array | undefined, tanks: Float32Array, stride: number, r: number, now: number): void {
  if (!patches?.length) return;
  ctx.save();
  ctx.strokeStyle = '#d7e6ef';
  ctx.lineWidth = 1.5;
  for (let i = 0; i < tanks.length / stride; i++) {
    const o = i * stride;
    if (tanks[o + 4] !== 1 || !onPatch(patches, PUDDLE, tanks[o], tanks[o + 1])) continue;
    for (const shift of [0, 0.5]) {
      const q = (now / 900 + shift + i * 0.3) % 1;
      ctx.globalAlpha = 0.7 * (1 - q);
      ctx.beginPath();
      ctx.arc(tanks[o], tanks[o + 1], r * (1 + q), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Heat shimmer: wavy, rising streaks of light over the hot patches. */
export function drawHeat(ctx: CanvasRenderingContext2D, patches: Float32Array | undefined, now: number): void {
  if (!patches) return;
  ctx.save();
  eachPatch(patches, (kind, at, corners) => {
    if (kind !== HEAT) return;
    ctx.save();
    patchPath(ctx, patches, at, corners);
    ctx.clip();
    const [x, y, r] = middle(patches, at, corners);
    ctx.strokeStyle = 'rgba(255, 245, 225, 0.22)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let k = 0; k < 5; k++) {
      const rise = ((now / 2500 + k / 7) % 1) * 2 * r;
      const yy = y + r - rise;
      ctx.moveTo(x - r, yy + Math.sin(-r / 9 + now / 180 + k) * 3);
      for (let u = -r; u <= r; u += 6) ctx.lineTo(x + u, yy + Math.sin(u / 9 + now / 180 + k) * 3);
    }
    ctx.stroke();
    ctx.restore();
  });
  ctx.restore();
}

/**
 * Heat shimmer over what is already drawn in the heat patches (ground, tanks): thin rows of the
 * picture shifted to and fro, rippling upwards.
 */
/** Scratch copy of a heat patch's piece of the picture: copying the canvas onto itself row by
 * row costs far too much (a whole frame per row in some browsers). */
const shimmerCopy = document.createElement('canvas');

export function drawShimmer(ctx: CanvasRenderingContext2D, patches: Float32Array | undefined, now: number): void {
  if (!patches) return;
  const m = ctx.getTransform();
  const canvas = ctx.canvas;
  eachPatch(patches, (kind, at, corners) => {
    if (kind !== HEAT) return;
    const [x, y, r] = middle(patches, at, corners);
    const [left, top] = [Math.max(0, Math.floor(m.a * (x - r) + m.e)), Math.max(0, Math.floor(m.d * (y - r) + m.f))];
    const [right, bottom] = [Math.min(canvas.width, Math.ceil(m.a * (x + r) + m.e)), Math.min(canvas.height, Math.ceil(m.d * (y + r) + m.f))];
    if (right <= left || bottom <= top) return;
    ctx.save();
    patchPath(ctx, patches, at, corners);
    ctx.clip();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const [w, h] = [right - left, bottom - top];
    // As big as the picture: a small canvas is not on the graphics card, and copying into it
    // makes the browser wait for the card every frame (frames of 50 ms).
    if (shimmerCopy.width !== canvas.width || shimmerCopy.height !== canvas.height) [shimmerCopy.width, shimmerCopy.height] = [canvas.width, canvas.height];
    const copy = shimmerCopy.getContext('2d')!;
    copy.clearRect(left, top, w, h);
    copy.drawImage(canvas, left, top, w, h, left, top, w, h);
    const row = Math.max(2, Math.round(m.d * 2.5));
    for (let sy = 0; sy < h; sy += row) {
      const shift = Math.sin((top + sy) / (m.d * 7) + now / 300) * Math.sin(now / 2400 + (top + sy) / (m.d * 40)) * 1.4 * m.a;
      const rh = Math.min(row, h - sy);
      ctx.drawImage(shimmerCopy, left, top + sy, w, rh, left + shift, top + sy, w, rh);
    }
    ctx.restore();
  });
}

/** Whether `x`, `y` lies in one of `fogBanks` (polygons as walls). */
export function inFogBank(fogBanks: Float32Array | undefined, x: number, y: number): boolean {
  if (!fogBanks) return false;
  for (let i = 0; i < fogBanks.length; ) {
    const corners = fogBanks[i++];
    let inside = false;
    for (let a = 0, b = corners - 1; a < corners; b = a++) {
      const [ax, ay, bx, by] = [fogBanks[i + 2 * a], fogBanks[i + 2 * a + 1], fogBanks[i + 2 * b], fogBanks[i + 2 * b + 1]];
      if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
    }
    if (inside) return true;
    i += 2 * corners;
  }
  return false;
}

/**
 * Fog banks as billowing clouds: a dense core over the bank (it must hide who is in it) and
 * soft puffs round it that swell and drift, so its edge wavers.
 */
function drawFogBanks(ctx: CanvasRenderingContext2D, fogBanks: Float32Array, now: number): void {
  ctx.save();
  for (let i = 0, bank = 0; i < fogBanks.length; bank++) {
    const corners = fogBanks[i++];
    const at = i;
    i += 2 * corners;
    const [x, y, r] = middle(fogBanks, at, corners);
    // Core: the bank itself, a little transparent. The banks only drift, so its soft edge (a
    // blur, dear to draw) is drawn once into a picture that moves along.
    const core = fogCore(ctx, bank, fogBanks, at, corners);
    ctx.drawImage(core.canvas, fogBanks[at] + core.x, fogBanks[at + 1] + core.y, core.size, core.size);
    // Puffs: round the rim and inside, each breathing and circling at its own pace.
    for (let k = 0; k < 14; k++) {
      const t = now / 1000;
      const a = (k / 14) * Math.PI * 2 + t * (0.05 + 0.02 * (k % 3)) + bank;
      const d = r * (k % 2 ? 0.85 : 0.4) * (1 + 0.12 * Math.sin(t * 0.7 + k * 1.7));
      const px = x + Math.cos(a) * d;
      const py = y + Math.sin(a) * d;
      const pr = r * (0.42 + 0.12 * Math.sin(t * 0.9 + k * 2.3));
      ctx.drawImage(puffPicture(), px - pr, py - pr, 2 * pr, 2 * pr);
    }
  }
  ctx.restore();
}

/** Blur of the fog bank core in canvas pixels. */
const FOG_BLUR = 30;
/** Fog bank cores as pictures, per bank: its shape (corners from the first), scale, and where the picture starts from the first corner. */
const fogCores: { shape: Float32Array; scale: number; canvas: HTMLCanvasElement; x: number; y: number; size: number }[] = [];

function fogCore(ctx: CanvasRenderingContext2D, bank: number, banks: Float32Array, at: number, corners: number): { canvas: HTMLCanvasElement; x: number; y: number; size: number } {
  const m = ctx.getTransform();
  const scale = Math.hypot(m.a, m.b);
  const shape = new Float32Array(2 * corners);
  for (let k = 0; k < 2 * corners; k++) shape[k] = banks[at + k] - banks[at + (k % 2)];
  const cached = fogCores[bank];
  if (cached && cached.scale === scale && cached.shape.length === shape.length && cached.shape.every((v, k) => Math.abs(v - shape[k]) < 0.01)) return cached; // moving rounds a little
  const [x, y, r] = middle(shape, 0, corners);
  const pad = (2 * FOG_BLUR) / scale; // the blur reaches out about twice its value
  const px = Math.ceil(2 * (r + pad) * scale);
  const canvas = cached?.canvas ?? document.createElement('canvas');
  canvas.width = canvas.height = px;
  const g = canvas.getContext('2d')!;
  g.setTransform(scale, 0, 0, scale, (pad + r - x) * scale, (pad + r - y) * scale);
  g.beginPath();
  for (let k = 0; k < corners; k++) g.lineTo(shape[2 * k], shape[2 * k + 1]);
  g.closePath();
  g.globalAlpha = 0.82;
  g.fillStyle = '#eceff0';
  g.shadowColor = '#eceff0';
  g.shadowBlur = FOG_BLUR; // a soft edge
  g.fill();
  const core = { shape, scale, canvas, x: x - r - pad, y: y - r - pad, size: px / scale };
  fogCores[bank] = core;
  return core;
}

/** One fog puff (a soft white disc), drawn once and scaled: a gradient per puff and frame costs. */
let puff: HTMLCanvasElement | undefined;

function puffPicture(): HTMLCanvasElement {
  if (puff) return puff;
  puff = document.createElement('canvas');
  const size = 128;
  puff.width = puff.height = size;
  const g = puff.getContext('2d')!;
  const fill = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  fill.addColorStop(0, 'rgba(246, 248, 249, 0.75)');
  fill.addColorStop(0.6, 'rgba(240, 243, 245, 0.35)');
  fill.addColorStop(1, 'rgba(240, 243, 245, 0)');
  g.fillStyle = fill;
  g.fillRect(0, 0, size, size);
  return puff;
}

/** Whether `x`, `y` lies in one of the smoke clouds `smoke` (stride: x, y, share left) of `radius`. */
export function inSmoke(smoke: Float32Array | undefined, radius: number, x: number, y: number): boolean {
  if (!smoke) return false;
  for (let i = 0; i < smoke.length; i += 3) if (Math.hypot(x - smoke[i], y - smoke[i + 1]) < radius) return true;
  return false;
}

/**
 * Smoke clouds of the smoke grenade: like a fog bank, but grey and round. A dense core over the
 * cloud (it must hide who is in it) and puffs that billow round it; it swells up in its first
 * moments and thins out in its last.
 */
export function drawSmoke(ctx: CanvasRenderingContext2D, smoke: Float32Array, radius: number, now: number): void {
  const t = now / 1000;
  ctx.save();
  for (let i = 0; i < smoke.length; i += 3) {
    const [x, y, left] = [smoke[i], smoke[i + 1], smoke[i + 2]];
    const grow = Math.min(1, (1 - left) / SMOKE_SWELL);
    const r = radius * (0.6 + 0.4 * grow);
    ctx.globalAlpha = Math.min(1, left / SMOKE_THIN);
    const core = 1.3 * r;
    ctx.drawImage(smokePicture(), x - core, y - core, 2 * core, 2 * core);
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2 + t * (0.3 + 0.1 * (k % 3)) + x;
      const d = r * (k % 2 ? 0.75 : 0.35) * (1 + 0.15 * Math.sin(t * 1.3 + k * 1.7));
      const pr = r * (0.55 + 0.12 * Math.sin(t * 1.7 + k * 2.3));
      ctx.drawImage(smokePicture(), x + Math.cos(a) * d - pr, y + Math.sin(a) * d - pr, 2 * pr, 2 * pr);
    }
  }
  ctx.restore();
}

/** Share of a smoke cloud's time it swells up in, and the last share it thins out in. */
const SMOKE_SWELL = 0.08;
const SMOKE_THIN = 0.12;

/** One smoke puff (a soft grey disc, dense in the middle), drawn once and scaled. */
let smokePuff: HTMLCanvasElement | undefined;

function smokePicture(): HTMLCanvasElement {
  if (smokePuff) return smokePuff;
  smokePuff = document.createElement('canvas');
  const size = 128;
  smokePuff.width = smokePuff.height = size;
  const g = smokePuff.getContext('2d')!;
  const fill = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  fill.addColorStop(0, 'rgba(120, 122, 124, 0.95)');
  fill.addColorStop(0.55, 'rgba(130, 132, 134, 0.8)');
  fill.addColorStop(1, 'rgba(140, 142, 144, 0)');
  g.fillStyle = fill;
  g.fillRect(0, 0, size, size);
  return smokePuff;
}

/** From inside a fog bank (or smoke) one sees only `near` around oneself: all else in `veil` (r, g, b). */
export function drawBlind(ctx: CanvasRenderingContext2D, x: number, y: number, near: number, width: number, height: number, veil = '236, 240, 242'): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.clip();
  const fill = ctx.createRadialGradient(x, y, near * 0.6, x, y, near * 1.4);
  fill.addColorStop(0, `rgba(${veil}, 0)`);
  fill.addColorStop(1, `rgba(${veil}, 0.93)`);
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

/** A spring shower: slanting streaks and a darker sky; `density` × the drops (close-ups in the guide). */
export function drawRain(ctx: CanvasRenderingContext2D, width: number, height: number, now: number, density = 1): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.clip();
  ctx.fillStyle = 'rgba(40, 55, 70, 0.18)';
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = 'rgba(210, 225, 240, 0.55)';
  ctx.lineWidth = 1;
  const drops = Math.round((density * width * height) / 3500);
  ctx.beginPath();
  for (let k = 0; k < drops; k++) {
    const seed = Math.sin(k * 39.346) * 43758.5453;
    const f = seed - Math.floor(seed);
    const x = (f * 9973 + k * 13) % width;
    const y = (((k * 51.3 + (now / 1000) * (500 + 200 * f)) % height) + height) % height;
    ctx.moveTo(x, y);
    ctx.lineTo(x - 3, y + 12);
  }
  ctx.stroke();
  ctx.restore();
}

/** Whether `x`, `y` lies under leaves. */
export function underLeaves(patches: Float32Array | undefined, x: number, y: number): boolean {
  return !!patches && onPatch(patches, LEAVES, x, y);
}

/** Whether `x`, `y` lies on a patch of `kind`. */
/** Colours (cloud, specks) of a bullet puffing away at `x`, `y`: leafy in a hedge, earthy on a hill, else grey. */
export function puffColors(patches: Float32Array | undefined, x: number, y: number): [string, string] {
  if (patches && onPatch(patches, HEDGE, x, y)) return ['#a5d16b', '#2c4f23'];
  if (patches && onPatch(patches, HILL, x, y)) return ['#9a7a52', '#5e4527'];
  return ['#8c8c84', '#55554f'];
}

/** Whether `x`, `y` lies on a patch of `kind`. */
export function onPatch(patches: Float32Array, kind: number, x: number, y: number): boolean {
  let inside = false;
  eachPatch(patches, (k, at, corners) => {
    if (inside || k !== kind) return;
    let crossings = false;
    for (let a = 0, b = corners - 1; a < corners; b = a++) {
      const [ax, ay, bx, by] = [patches[at + 2 * a], patches[at + 2 * a + 1], patches[at + 2 * b], patches[at + 2 * b + 1]];
      if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) crossings = !crossings;
    }
    inside = crossings;
  });
  return inside;
}

const RUT_SPACING = 10;
/** Ruts per ground: how long they stay, colour, how many at most. */
const RUTS: { ground: number; ms: number; color: string; alpha: number; max: number }[] = [
  { ground: SNOW, ms: 8000, color: '#8796a3', alpha: 0.55, max: 800 },
  { ground: MUD, ms: 8000, color: '#3e2b1a', alpha: 0.5, max: 800 },
  { ground: SAND, ms: 5000, color: '#b39456', alpha: 0.5, max: 800 },
];

/** A cheap fingerprint of the patches: a new round, new ones (ruts start over). */
const patchKey = (p: Float32Array) => `${p.length}:${p[2]}:${p[3]}:${p[p.length - 1]}`;

/**
 * Ruts every tank leaves (whatever it wears) in deep snow, mud and sand, fading (in sand they
 * blow away sooner).
 */
export class Ruts {
  private ruts: { x: number; y: number; angle: number; len: number; start: number; kind: number }[][] = RUTS.map(() => []);
  private last: ({ x: number; y: number } | undefined)[] = [];
  private key = '';

  /** Lays ruts behind the tanks; `visible(i)` false: none (dead, cloaked, unseen). */
  lay(tanks: Float32Array, stride: number, patches: Float32Array | undefined, visible: (i: number) => boolean, now: number): void {
    const key = patches ? patchKey(patches) : '';
    if (key !== this.key) {
      this.key = key;
      this.ruts = RUTS.map(() => []);
    }
    RUTS.forEach((kind, k) => {
      const ruts = this.ruts[k];
      while (ruts.length && now - ruts[0].start > kind.ms) ruts.shift();
    });
    if (!patches?.length) {
      this.last = [];
      return;
    }
    for (let i = 0; i < tanks.length / stride; i++) {
      const o = i * stride;
      const [x, y] = [tanks[o], tanks[o + 1]];
      const last = this.last[i];
      const kind = visible(i) ? RUTS.findIndex((r) => onPatch(patches, r.ground, x, y)) : -1;
      if (kind < 0) {
        this.last[i] = undefined;
        continue;
      }
      const d = last ? Math.hypot(x - last.x, y - last.y) : Infinity;
      if (d < RUT_SPACING) continue;
      if (last && d <= RUT_SPACING * 4) this.ruts[kind].push({ x: (x + last.x) / 2, y: (y + last.y) / 2, angle: tanks[o + 2], len: d, start: now, kind });
      this.last[i] = { x, y };
    }
    RUTS.forEach((kind, k) => {
      const ruts = this.ruts[k];
      if (ruts.length > kind.max) ruts.splice(0, ruts.length - kind.max);
    });
  }

  draw(ctx: CanvasRenderingContext2D, r: number, now: number): void {
    ctx.save();
    RUTS.forEach((kind, k) => {
      ctx.fillStyle = kind.color;
      for (const rut of this.ruts[k]) {
        const left = 1 - (now - rut.start) / kind.ms;
        ctx.globalAlpha = kind.alpha * left;
        ctx.save();
        ctx.translate(rut.x, rut.y);
        ctx.rotate(rut.angle);
        // Sand blows into them: they get shallower as they fade.
        const deep = kind.ground === SAND ? 0.4 * (0.4 + 0.6 * left) : 0.4;
        for (const side of [-0.65 * r, 0.65 * r]) ctx.fillRect(-rut.len / 2, side - r * deep * 0.5, rut.len, r * deep);
        ctx.restore();
      }
    });
    ctx.restore();
  }
}

const SWIRL_MS = 1300; // a leaf thrown up is back down after this long
const HEDGE_LEAVES = ['#3f6d32', '#2c4f23', '#5f8f3e', '#f2f2f2', '#f5c6d6'];

/**
 * Leaves thrown up by the tanks: from leaf piles when a tank drives through, green ones (and
 * blossoms) where it breaks through a hedge.
 */
export class LeafSwirl {
  private leaves: { x: number; y: number; vx: number; vy: number; up: number; turn: number; spin: number; color: string; start: number }[] = [];
  private last: ({ x: number; y: number } | undefined)[] = [];

  /** Throws leaves up behind the moving tanks in leaf piles and hedges; `visible(i)` false: none. */
  lay(tanks: Float32Array, stride: number, patches: Float32Array | undefined, visible: (i: number) => boolean, now: number): void {
    this.leaves = this.leaves.filter((l) => now - l.start < SWIRL_MS);
    if (!patches?.length) return;
    for (let i = 0; i < tanks.length / stride; i++) {
      const o = i * stride;
      const [x, y] = [tanks[o], tanks[o + 1]];
      const last = this.last[i];
      this.last[i] = { x, y };
      if (!last || !visible(i)) continue;
      const colors = onPatch(patches, LEAVES, x, y) ? LEAF_COLORS : onPatch(patches, HEDGE, x, y) ? HEDGE_LEAVES : undefined;
      if (!colors) continue;
      const moved = Math.hypot(x - last.x, y - last.y);
      // More the faster it goes, none standing.
      if (moved < 0.5 || Math.random() > moved / 3) continue;
      this.throw(x, y, tanks[o + 2] + Math.PI, colors, now);
    }
  }

  /** One leaf from `x`, `y`, flying off roughly towards `angle`. */
  throw(x: number, y: number, angle: number, colors: string[], now: number): void {
    if (this.leaves.length > 300) return;
    const a = angle + (Math.random() - 0.5) * 2.2;
    const speed = 25 + Math.random() * 45;
    this.leaves.push({
      x: x + (Math.random() - 0.5) * 20,
      y: y + (Math.random() - 0.5) * 20,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed,
      up: 0.6 + Math.random() * 0.6,
      turn: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 12,
      color: colors[Math.floor(Math.random() * colors.length)],
      start: now,
    });
  }

  draw(ctx: CanvasRenderingContext2D, now: number): void {
    ctx.save();
    for (const l of this.leaves) {
      const t = (now - l.start) / 1000;
      const p = t / (SWIRL_MS / 1000);
      // Up and down again: bigger while high, a shadow below; slowing down in the air.
      const high = Math.sin(Math.PI * p) * l.up;
      const drift = (1 - Math.exp(-2.5 * t)) / 2.5;
      const [x, y] = [l.x + l.vx * drift, l.y + l.vy * drift - high * 14];
      ctx.globalAlpha = 0.25 * (1 - p);
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(l.x + l.vx * drift, l.y + l.vy * drift, 4, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = p > 0.85 ? (1 - p) / 0.15 : 1;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(l.turn + l.spin * t);
      const size = 1 + high * 0.6;
      ctx.fillStyle = l.color;
      ctx.beginPath();
      // Seen from above while it tumbles: wider and narrower.
      ctx.ellipse(0, 0, 6 * size, 3 * size * Math.abs(Math.cos(t * 7 + l.turn)) + 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  }
}

const HEDGE_OPEN_MS = 250; // a hedge tears open this fast
const HEDGE_GROW_MS = 3000; // and grows back together in this long

/** Gaps tanks tear into the hedges, growing back together (only drawn: hedges still stop bullets). */
export class HedgeGaps {
  private gaps: { x: number; y: number; start: number; last: number }[] = [];
  private key = '';

  lay(tanks: Float32Array, stride: number, patches: Float32Array | undefined, visible: (i: number) => boolean, now: number): void {
    const key = patches ? patchKey(patches) : '';
    if (key !== this.key) [this.key, this.gaps] = [key, []];
    this.gaps = this.gaps.filter((g) => now - g.last < HEDGE_GROW_MS);
    if (!patches?.length) return;
    for (let i = 0; i < tanks.length / stride; i++) {
      const o = i * stride;
      const [x, y] = [tanks[o], tanks[o + 1]];
      if (!visible(i) || !onPatch(patches, HEDGE, x, y)) continue;
      const gap = this.gaps.find((g) => Math.hypot(g.x - x, g.y - y) < 10);
      if (gap) [gap.x, gap.y, gap.last] = [x, y, now];
      else this.gaps.push({ x, y, start: now, last: now });
    }
  }

  /** The gaps: the floor showing through, torn twigs at the edge; `floor`: its colour. */
  draw(ctx: CanvasRenderingContext2D, patches: Float32Array | undefined, floor: string, r: number, now: number): void {
    if (!patches || !this.gaps.length) return;
    ctx.save();
    ctx.beginPath();
    eachPatch(patches, (kind, at, corners) => {
      if (kind !== HEDGE) return;
      ctx.moveTo(patches[at], patches[at + 1]);
      for (let k = 1; k < corners; k++) ctx.lineTo(patches[at + 2 * k], patches[at + 2 * k + 1]);
      ctx.closePath();
    });
    ctx.clip();
    for (const g of this.gaps) {
      const opening = Math.min(1, (now - g.start) / HEDGE_OPEN_MS);
      const open = opening * (1 - (now - g.last) / HEDGE_GROW_MS);
      if (open <= 0) continue;
      const size = r * 1.15 * open;
      ctx.beginPath();
      ctx.arc(g.x, g.y, size, 0, Math.PI * 2);
      ctx.fillStyle = floor;
      ctx.fill();
      // Torn twigs round the edge, shorter as it closes.
      ctx.strokeStyle = '#2c4f23';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let k = 0; k < 14; k++) {
        const a = (k / 14) * Math.PI * 2 + g.x;
        const len = 3 + 4 * Math.abs(Math.sin(k * 2.3 + g.y));
        ctx.moveTo(g.x + Math.cos(a) * (size + 2), g.y + Math.sin(a) * (size + 2));
        ctx.lineTo(g.x + Math.cos(a + 0.3) * (size - len * open), g.y + Math.sin(a + 0.3) * (size - len * open));
      }
      ctx.stroke();
    }
    ctx.restore();
  }
}

/** Falling snow over the map (world units), with a light veil; `density` as in `drawRain`. */
export function drawSnowfall(ctx: CanvasRenderingContext2D, width: number, height: number, now: number, density = 1): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.clip();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#ffffff';
  const flakes = Math.round((density * width * height) / 2500);
  // One path per step of opacity: a fill per flake costs more than the whole rest of the frame.
  for (let step = 0; step < WEATHER_STEPS; step++) {
    ctx.globalAlpha = 0.5 + (0.5 * (step + 0.5)) / WEATHER_STEPS;
    ctx.beginPath();
    for (let k = 0; k < flakes; k++) {
      // Each flake falls at its own speed and sways; positions wrap around the map.
      const seed = Math.sin(k * 12.9898) * 43758.5453;
      const f = seed - Math.floor(seed);
      if (Math.floor(f * WEATHER_STEPS) !== step) continue;
      const speed = 25 + 35 * f;
      const x = (((f * 7919 + Math.sin(now / 900 + k) * 8) % width) + width) % width;
      const y = (((k * 37.7 + (now / 1000) * speed) % height) + height) % height;
      const r = 1 + 1.3 * f;
      ctx.moveTo(x + r, y);
      ctx.arc(x, y, r, 0, Math.PI * 2);
    }
    ctx.fill();
  }
  ctx.restore();
}

/** Streaks over the map move this many units/s per unit of the wind's pull. */
const STREAK_PACE = 8;

/** Wind (pull x, y; units/s²): streaks blowing across the map — leaves in autumn, snow in winter — and a vane in the top left corner; `density` as in `drawRain`. */
export function drawWind(ctx: CanvasRenderingContext2D, wind: Float32Array | undefined, season: number | undefined, width: number, height: number, now: number, density = 1): void {
  if (!wind || wind.length < 2) return;
  const [wx, wy] = [wind[0], wind[1]];
  const pull = Math.hypot(wx, wy);
  const [dx, dy] = [wx / pull, wy / pull];
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.clip();
  // Streaks: each its own lane across, at a pace you can follow that grows with the gust
  // (pull 7 → 56 units/s), and thicker in strong gusts.
  const streaks = Math.round((density * width * height) / (season === AUTUMN ? 30000 : 12000));
  const speed = STREAK_PACE * pull;
  const gust = Math.min(1, pull / 10);
  // One path per leaf colour or step of opacity, as in `drawSnowfall`.
  const autumn = season === AUTUMN;
  const groups = autumn ? LEAF_COLORS.length : WEATHER_STEPS;
  ctx.globalAlpha = 0.8;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.2;
  for (let group = 0; group < groups; group++) {
    if (autumn) ctx.fillStyle = LEAF_COLORS[group];
    else ctx.globalAlpha = (0.25 + (0.3 * (group + 0.5)) / WEATHER_STEPS) * (0.6 + 0.4 * gust);
    ctx.beginPath();
    for (let k = 0; k < streaks; k++) {
      const seed = Math.sin(k * 78.233) * 43758.5453;
      const f = seed - Math.floor(seed);
      if ((autumn ? k % LEAF_COLORS.length : Math.floor(f * WEATHER_STEPS)) !== group) continue;
      const run = ((now / 1000) * speed * (0.7 + 0.6 * f) + k * 97) % (width + height);
      const across = f * (width + height) - height;
      const x = (((across * -dy + run * dx) % width) + width) % width;
      const y = (((across * dx + run * dy) % height) + height) % height;
      if (autumn) {
        const turn = now / 200 + k;
        ctx.moveTo(x + Math.cos(turn) * 5, y + Math.sin(turn) * 5);
        ctx.ellipse(x, y, 5, 2.5, turn, 0, Math.PI * 2);
      } else {
        ctx.moveTo(x, y);
        ctx.lineTo(x - dx * 14, y - dy * 14);
      }
    }
    if (autumn) ctx.fill();
    else ctx.stroke();
  }
  ctx.restore();
  // The vane: a disc with an arrow downwind, longer in gusts.
  const [cx, cy, r] = [34, 34, 22];
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = '#1b1f14cc';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  const len = r * Math.min(1, 0.35 + pull / 14);
  ctx.strokeStyle = '#f2ead0';
  ctx.fillStyle = '#f2ead0';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx - dx * len, cy - dy * len);
  ctx.lineTo(cx + dx * len * 0.6, cy + dy * len * 0.6);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx + dx * len, cy + dy * len);
  ctx.lineTo(cx + dx * len * 0.4 - dy * 7, cy + dy * len * 0.4 + dx * 7);
  ctx.lineTo(cx + dx * len * 0.4 + dy * 7, cy + dy * len * 0.4 - dx * 7);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
